import React, { useState } from 'react';
import { ServiceOrderWithTasks, ServiceOrderStatusColors } from '../../types/serviceOrder';
import { ServiceOrderBar } from './ServiceOrderBar';

interface GanttRowProps {
  project: {
    id: string;
    name: string;
    serviceOrders: ServiceOrderWithTasks[];
  };
  dateRange: { start: Date; end: Date };
  getPositionForDate: (date: Date) => number;
  getServiceOrderWidth: (serviceOrder: ServiceOrderWithTasks) => number;
  onServiceOrderClick: (projectId: string, serviceOrderId: string) => void;
}

export const GanttRow: React.FC<GanttRowProps> = ({
  project,
  dateRange,
  getPositionForDate,
  getServiceOrderWidth,
  onServiceOrderClick
}) => {
  const [hoveredServiceOrder, setHoveredServiceOrder] = useState<string | null>(null);

  // Sort service orders by start date
  const sortedServiceOrders = [...project.serviceOrders].sort((a, b) => 
    new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );

  // Group overlapping service orders into layers
  const organizeServiceOrdersIntoLayers = (serviceOrders: ServiceOrderWithTasks[]) => {
    const layers: ServiceOrderWithTasks[][] = [];
    
    serviceOrders.forEach(serviceOrder => {
      const startDate = new Date(serviceOrder.startDate);
      const endDate = new Date(serviceOrder.endDate);
      
      // Find the first layer where this service order doesn't overlap
      let layerFound = false;
      for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        const hasOverlap = layer.some(existingOrder => {
          const existingStart = new Date(existingOrder.startDate);
          const existingEnd = new Date(existingOrder.endDate);
          return (startDate < existingEnd && endDate > existingStart);
        });
        
        if (!hasOverlap) {
          layer.push(serviceOrder);
          layerFound = true;
          break;
        }
      }
      
      // If no layer found, create a new one
      if (!layerFound) {
        layers.push([serviceOrder]);
      }
    });
    
    return layers;
  };

  const layers = organizeServiceOrdersIntoLayers(sortedServiceOrders);
  const rowHeight = Math.max(48, layers.length * 28 + 20); // Dynamic height based on layers

  return (
    <div className="border border-white/10 rounded-lg">
      {/* Project Header */}
      <div className="bg-white/5 p-3 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-foreground-primary">
              {project.name}
            </h3>
            <p className="text-xs text-foreground-tertiary">
              {project.serviceOrders.length} ordem{project.serviceOrders.length !== 1 ? 's' : ''} de serviço
            </p>
          </div>
          <div className="text-xs text-foreground-tertiary">
            {layers.length} camada{layers.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Timeline Area */}
      <div className="relative bg-white/2" style={{ height: rowHeight }}>
        {/* Background grid */}
        <div className="absolute inset-0 grid grid-cols-12 gap-0">
          {Array.from({ length: 12 }).map((_, index) => (
            <div key={index} className="border-r border-white/5 last:border-r-0" />
          ))}
        </div>

        {/* Service Order Bars */}
        {layers.map((layer, layerIndex) => (
          <div
            key={layerIndex}
            className="absolute inset-x-0"
            style={{
              top: `${layerIndex * 28 + 10}px`,
              height: '24px'
            }}
          >
            {layer.map((serviceOrder) => {
              const startDate = new Date(serviceOrder.startDate);
              const endDate = new Date(serviceOrder.endDate);
              const position = getPositionForDate(startDate);
              const width = getServiceOrderWidth(serviceOrder);

              // Skip if completely outside the visible range
              if (position > 100 || position + width < 0) {
                return null;
              }

              // Clamp position and width to visible area
              const clampedPosition = Math.max(0, position);
              const clampedWidth = Math.min(100 - clampedPosition, width);

              return (
                <ServiceOrderBar
                  key={serviceOrder._id}
                  serviceOrder={serviceOrder}
                  position={clampedPosition}
                  width={clampedWidth}
                  isHovered={hoveredServiceOrder === serviceOrder._id}
                  onMouseEnter={() => setHoveredServiceOrder(serviceOrder._id)}
                  onMouseLeave={() => setHoveredServiceOrder(null)}
                  onClick={() => onServiceOrderClick(project.id, serviceOrder._id)}
                />
              );
            })}
          </div>
        ))}

        {/* Empty state */}
        {project.serviceOrders.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-xs text-foreground-tertiary">
              Nenhuma ordem de serviço
            </div>
          </div>
        )}
      </div>
    </div>
  );
};